import React, { useState, useEffect } from 'react';
import CurrencySelector from '@/components/CurrencySelector';
import { CURRENCY_SYMBOLS } from '@/hooks/useCurrency';
import { BACKEND_URL as API_URL } from '@/lib/backendUrl';

// Окно «Оплата иностранной картой» (Stripe): выбор валюты и кнопка с суммой.
// Вынесено из PricingPage без изменений, чтобы им пользовалась и страница
// оформления (/checkout). PricingPage реэкспортирует его под прежним именем.

// «5 000 ₸», «9,99 $»: разряды пробелом, копейки — только если они есть.
export const formatStripeAmount = (amount, symbol) =>
  `${new Intl.NumberFormat('ru-RU', { maximumFractionDigits: 2 }).format(amount)}\u00A0${symbol}`;

// Сумма к оплате в выбранной валюте — тем же расчётом, что и Stripe Checkout
// (GET /api/stripe/checkout-price → get_checkout_price на бэкенде), поэтому
// на кнопке ровно то, что человек увидит в Stripe. Пока суммы нет (грузится,
// ошибка) — прежняя подпись без суммы.
function useStripeQuote(isOpen, planCode, currency) {
  const [quote, setQuote] = useState(null);
  useEffect(() => {
    setQuote(null);
    if (!isOpen || !planCode) return undefined;
    const token = localStorage.getItem('auth_token');
    if (!token) return undefined;
    let cancelled = false;
    const qs = new URLSearchParams({ plan: planCode, currency: currency || '' });
    fetch(`${API_URL}/api/stripe/checkout-price?${qs}`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        if (!cancelled && data && Number(data.amount) > 0) setQuote(data);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [isOpen, planCode, currency]);
  return quote;
}

// Срок под суммой строки окна: «/мес», «/год», «навсегда».
export function stripePlanSuffix(planCode) {
  const code = String(planCode || '').toUpperCase();
  if (code.endsWith('_MONTHLY')) return '/мес';
  if (code.endsWith('_YEARLY')) return '/год';
  if (code === 'FOUNDER') return ' навсегда';
  return '';
}

// «день / дня / дней» — для строки подарка под ценой.
export function giftDaysWord(n) {
  const a = Math.abs(n) % 100;
  const b = a % 10;
  if (b === 1 && a !== 11) return 'день';
  if (b >= 2 && b <= 4 && (a < 12 || a > 14)) return 'дня';
  return 'дней';
}

// Payment Modal Component - NOW ONLY FOR STRIPE (secondary option)
//
// priceFromQuote — строка «ПРО — …» над кнопкой показывает ту же сумму и
// валюту, что кнопка (ответ /api/stripe/checkout-price), и меняется вместе с
// валютой. Включается для иностранца на ру; без флага — прежняя строка price.
const StripePaymentModal = ({ isOpen, onClose, plan, planCode, price, onPay, currency, onCurrencyChange, availableCurrencies, priceFromQuote = false }) => {
  const quote = useStripeQuote(isOpen, planCode, currency);
  if (!isOpen) return null;
  const quoteAmount = quote
    ? formatStripeAmount(Number(quote.amount), quote.symbol || CURRENCY_SYMBOLS[quote.currency] || quote.currency)
    : '';
  const payLabel = quote
    ? `Оплатить ${quoteAmount}`
    : `Оплатить через Stripe — ${CURRENCY_SYMBOLS[currency] || currency}`;
  // Акция «первый месяц за 5 $»: amount — первое списание, regular_amount —
  // обычная цена со второго месяца (stripe_pro_offer.py на бэкенде).
  const regularAmount = quote && quote.promo && Number(quote.regular_amount) > 0
    ? formatStripeAmount(Number(quote.regular_amount), quote.symbol || CURRENCY_SYMBOLS[quote.currency] || quote.currency)
    : '';
  const priceLine = priceFromQuote
    ? (quote
      ? (regularAmount
        ? `первый месяц ${quoteAmount}, дальше ${regularAmount}${stripePlanSuffix(planCode)}`
        : `${quoteAmount}${stripePlanSuffix(planCode)}`)
      : '…')
    : price;
  const giftDays = quote ? Number(quote.gift_days) || 0 : 0;
  
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl p-6 max-w-sm w-full shadow-2xl" onClick={e => e.stopPropagation()}>
        <h3 className="text-lg font-bold text-slate-800 mb-4 text-center">
          Оплата иностранной картой
        </h3>
        <p className="text-center text-slate-600 mb-2" data-testid="stripe-modal-price-line">
          {plan} — {priceLine}
        </p>
        {giftDays > 0 && (
          <p className="text-center text-sm text-emerald-700 mb-2" data-testid="stripe-modal-gift-line">
            + {giftDays} {giftDaysWord(giftDays)} в подарок
          </p>
        )}
        
        {/* Currency selector for Stripe */}
        <div className="flex items-center justify-center gap-2 mb-4">
          <span className="text-sm text-slate-500">Валюта:</span>
          <CurrencySelector 
            currency={currency} 
            onSelect={onCurrencyChange}
            availableCurrencies={availableCurrencies}
            compact 
          />
        </div>
        
        <button
          onClick={() => onPay()}
          className="w-full py-3 bg-slate-800 text-white rounded-xl font-semibold hover:bg-slate-900 transition-colors"
          data-testid="pay-stripe-modal-btn"
        >
          {payLabel}
        </button>
        
        <button 
          onClick={onClose}
          className="w-full mt-3 py-2 text-slate-400 hover:text-slate-600 text-sm"
        >
          Отмена
        </button>
      </div>
    </div>
  );
};

export default StripePaymentModal;
